import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  uploadDocument,
  removeDocument,
  selectApplication,
  selectDocumentsByType,
  selectUploadingType,
  selectUploadError
} from '../../../redux/slices/applicationSlice';
import { StepCard, StepActions, Button, SectionRule } from '../../../components/stepCard';
import ImportPanel from '../../../components/importPanel';
import { MANDATORY_DOCUMENTS } from '../../../constants/wizard';
import PageTransition from '../../../components/pageTransition';
import { notify } from '../../../utils/notify';
import './index.scss';

const DOC_CONFIG = [
  {
    type: 'AADHAAR_CARD',
    label: 'Aadhaar card',
    hint: 'Front and back, in one file',
    mandatory: true
  },
  {
    type: 'PAN_CARD',
    label: 'PAN card',
    hint: 'Clear photo or scan',
    mandatory: true
  },
  {
    type: 'SALARY_SLIP',
    label: 'Salary slips',
    hint: 'Last 3 months',
    mandatory: true,
    multiple: true
  },
  {
    type: 'BANK_STATEMENT',
    label: 'Bank statement',
    hint: 'Last 6 months, from net banking',
    mandatory: true,
    multiple: true
  },
  {
    type: 'ITR',
    label: 'Income tax return',
    hint: 'Last 2 years, optional',
    mandatory: false,
    multiple: true
  },
  {
    type: 'FORM_16',
    label: 'Form 16',
    hint: 'Issued by your employer, optional',
    mandatory: false
  }
];

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED = ['application/pdf', 'image/jpeg', 'image/png'];

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// const FileIcon = () => (
//   <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
//     <path
//       d="M11.5 2.5H5.5a1 1 0 00-1 1v13a1 1 0 001 1h9a1 1 0 001-1V6.5l-4-4z"
//       stroke="currentColor"
//       strokeWidth="1.4"
//       strokeLinejoin="round"
//     />
//     <path d="M11.5 2.5v4h4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
//   </svg>
// );

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 16V4m0 0L8 8m4-4l4 4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 16v2.5A1.5 1.5 0 005.5 20h13a1.5 1.5 0 001.5-1.5V16"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const CheckDrawIcon = () => (
  <svg className="doc-check" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.4" opacity="0.3" />
    <path
      className="doc-check__tick"
      d="M6 10.5l2.5 2.5L14 7.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// One upload slot. Owns its own file input and drag state.
const DocumentSlot = ({ config, files, uploading, onUpload, onRemove }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Cheap client checks so an obviously bad file never leaves the browser.
  // The server re-checks magic bytes — this is convenience, not security.
  const accept = (file) => {
    if (!file) return false;
    if (!ACCEPTED.includes(file.type)) {
      setLocalError('Only PDF, JPG and PNG files are accepted');
      return false;
    }
    if (file.size > MAX_BYTES) {
      setLocalError('This file is over the 10MB limit');
      return false;
    }
    if (file.size === 0) {
      setLocalError('This file is empty');
      return false;
    }
    setLocalError(null);
    return true;
  };

  const handleFiles = (fileList) => {
    const file = fileList?.[0];
    if (accept(file)) onUpload(config.type, file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const hasFiles = files.length > 0;

  return (
    <div
      className={[
        'doc-slot',
        hasFiles && 'doc-slot--filled',
        dragging && 'doc-slot--dragging',
        localError && 'doc-slot--error'
      ]
        .filter(Boolean)
        .join(' ')}>
      <div className="doc-slot__head">
        <span className="doc-slot__label">
          {config.label}
          {config.mandatory && (
            <span className="doc-slot__req" aria-label="required">
              *
            </span>
          )}
        </span>
        <span className="doc-slot__hint">{config.hint}</span>
      </div>

      {hasFiles && (
        <ul className="doc-list">
          {files.map((file) => (
            <li key={file.id} className="doc-list__item">
              <span className="doc-list__icon">
                <CheckDrawIcon />
              </span>
              <span className="doc-list__meta">
                <span className="doc-list__name">{file.originalName}</span>
                <span className="doc-list__size numeric">{formatSize(file.sizeBytes)}</span>
              </span>
              <button
                type="button"
                className="doc-list__remove"
                onClick={() => onRemove(file.id)}
                aria-label={`Remove ${file.originalName}`}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {(!hasFiles || config.multiple) && (
        <button
          type="button"
          className="doc-drop"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          disabled={uploading}>
          {uploading ? (
            <>
              <span className="doc-drop__spinner" aria-hidden="true" />
              <span className="doc-drop__text">Uploading…</span>
            </>
          ) : (
            <>
              <span className="doc-drop__icon">
                <UploadIcon />
              </span>
              <span className="doc-drop__text">
                {hasFiles ? 'Add another file' : 'Choose a file or drop it here'}
              </span>
              <span className="doc-drop__sub">PDF, JPG or PNG · up to 10MB</span>
            </>
          )}
        </button>
      )}

      {hasFiles && !config.multiple && (
        <button
          type="button"
          className="doc-slot__replace"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}>
          {uploading ? 'Uploading…' : 'Replace file'}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = ''; // allow re-selecting the same file
        }}
      />

      {localError && (
        <span className="doc-slot__error" role="alert">
          {localError}
        </span>
      )}
    </div>
  );
};

const DocumentsStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const application = useSelector(selectApplication);
  const byType = useSelector(selectDocumentsByType);
  const uploadingType = useSelector(selectUploadingType);
  const uploadError = useSelector(selectUploadError);

  const handleUpload = async (docType, file) => {
    const result = await dispatch(uploadDocument({ id: application.id, docType, file }));
    if (uploadDocument.fulfilled.match(result)) {
      notify.success('Document uploaded');
    } else {
      notify.error(result.payload?.message ?? result.payload);
    }
  };

  const handleRemove = async (documentId) => {
    const result = await dispatch(removeDocument({ documentId }));
    if (removeDocument.fulfilled.match(result)) {
      notify.info('Document removed');
    } else {
      notify.error(result.payload);
    }
  };

  const uploadedTypes = new Set(Object.keys(byType));
  const missing = MANDATORY_DOCUMENTS.filter((t) => !uploadedTypes.has(t));
  const canContinue = missing.length === 0;

  const mandatoryDocs = DOC_CONFIG.filter((d) => d.mandatory);
  const optionalDocs = DOC_CONFIG.filter((d) => !d.mandatory);

  return (
    <PageTransition>
      <ImportPanel />

      <StepCard
        title="Upload your documents"
        description="Clear scans or photos. We accept PDF, JPG and PNG up to 10MB each.">
        <div className="doc-section">
          <SectionRule label="Required" />
          <div className="doc-grid">
            {mandatoryDocs.map((config) => (
              <DocumentSlot
                key={config.type}
                config={config}
                files={byType[config.type] ?? []}
                uploading={uploadingType === config.type}
                onUpload={handleUpload}
                onRemove={handleRemove}
              />
            ))}
          </div>

          <SectionRule label="Optional" />
          <div className="doc-grid">
            {optionalDocs.map((config) => (
              <DocumentSlot
                key={config.type}
                config={config}
                files={byType[config.type] ?? []}
                uploading={uploadingType === config.type}
                onUpload={handleUpload}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </div>

        {uploadError && (
          <p className="form-error" role="alert">
            {uploadError.message ?? uploadError}
          </p>
        )}

        {!canContinue && (
          <p className="doc-pending">
            {missing.length} required {missing.length === 1 ? 'document' : 'documents'} still to
            upload.
          </p>
        )}

        <StepActions onBack={() => navigate('/apply/loan')}>
          <Button type="button" disabled={!canContinue} onClick={() => navigate('/apply/review')}>
            Review application →
          </Button>
        </StepActions>
      </StepCard>
    </PageTransition>
  );
};
DocumentSlot.propTypes = {
  config: PropTypes.shape({
    type: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    hint: PropTypes.string,
    mandatory: PropTypes.bool,
    multiple: PropTypes.bool
  }).isRequired,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      originalName: PropTypes.string.isRequired,
      sizeBytes: PropTypes.number.isRequired
    })
  ).isRequired,
  uploading: PropTypes.bool,
  onUpload: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
};
export default DocumentsStep;
